// Single source of options for every `{ source: "reference", domain: ... }`
// field in a journey config.
//
// The rule this enforces: **the UI only ever speaks SmartSure's canonical code
// space.** It never sees, stores or sends a carrier's private code. Orient
// calls the UAE "4089" and the travel provider calls it "58"; both are
// translated on the backend, and the browser only knows "AE".
//
// That is what makes onboarding carrier number three a backend-only change:
// no screen, config or redux value has to learn a new code space.
//
// Responses are cached per (domain, params) for the session — reference data
// changes on a quarterly cadence at best, and the backend already TTL-caches
// upstream of us.

import apiClient from "../api/apiClient";
import { forget, forgetAll, keys, labelFor, remember } from "./labelCache";
import { keyFor } from "./referenceKey";

/** @typedef {{ code: string, label: string, hint?: string, aliases?: string[] }} ReferenceOption */

/** @type {Map<string, Promise<ReferenceOption[]>>} */
const cache = new Map();

/**
 * Fetch a canonical reference list.
 *
 * @param {string} domain   e.g. "nationality", "vehicle-make", "emirate"
 * @param {Record<string, string>} [params]  e.g. { make: "TOYOTA" } for dependent lists
 * @returns {Promise<ReferenceOption[]>}
 */
export const getReference = (domain, params) => {
  const key = keyFor(domain, params);
  if (cache.has(key)) return cache.get(key);

  const request = apiClient
    .get(`/reference/${encodeURIComponent(domain)}`, { params })
    .then(({ data }) => {
      const raw = Array.isArray(data) ? data : data?.items || [];
      const items = raw.map((item) => ({
        code: String(item.code ?? item.value ?? ""),
        label: String(item.label ?? item.text ?? item.code ?? ""),
        hint: item.hint,
        // Other spellings the same thing goes by, lent to a carrier's list by
        // the canonical catalogue. A document says "AMERICAN"; Orient's list
        // says "UNITED STATES".
        aliases: Array.isArray(item.aliases) ? item.aliases : undefined,
      }));
      remember(key, items);
      return items;
    })
    .catch((err) => {
      // A failed lookup must not poison the cache — the next mount retries.
      cache.delete(key);
      forget(key);
      throw err;
    });

  cache.set(key, request);
  return request;
};

/**
 * The label for a canonical code, if that domain has already been fetched.
 *
 * Synchronous and best-effort by design: it never triggers a request, and
 * returns "" when the list isn't loaded yet. Callers show the raw value in
 * that case, which is correct for a summary chip and wrong for a dropdown —
 * which is why dropdowns use the async path instead.
 *
 * @param {string} domain
 * @param {string} code
 * @param {Record<string, string>} [params]
 * @returns {string}
 */
export const peekReferenceLabel = (domain, code, params) =>
  labelFor(keyFor(domain, params), code);

/** Drop cached lists — call after a carrier config change in an admin screen. */
export const invalidateReference = (domain) => {
  if (!domain) {
    cache.clear();
    forgetAll();
    return;
  }
  for (const key of [...new Set([...cache.keys(), ...keys()])]) {
    if (key === domain || key.startsWith(`${domain}?`)) {
      cache.delete(key);
      forget(key);
    }
  }
};

/**
 * Best-effort match of free text (an OCR'd nationality, a pasted country name)
 * to a canonical code. Used to pre-fill selects from document extraction,
 * where the AI returns "U.A.E" / "United Arab Emirates" / "Emirati" for what
 * the list calls "United Arab Emirates".
 *
 * @param {ReferenceOption[]} options
 * @param {string} text
 * @returns {string} canonical code, or "" when nothing matches confidently
 */
export const matchReferenceText = (options, text) => {
  const raw = String(text || "").trim();
  if (!raw || !options?.length) return "";

  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");
  const normAlnum = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const initials = (s) =>
    String(s || "")
      .trim()
      .split(/\s+/)
      .map((w) => w[0] || "")
      .join("")
      .toLowerCase();

  // An exact code hit wins outright — the AI sometimes returns the code itself.
  //
  // Codes keep their digits. `norm` strips everything that isn't a letter,
  // which is right for labels ("U.A.E" → "uae") but reduced every one of
  // Orient's numeric make codes ("00142") to the empty string, so this branch
  // could never fire for a numeric code space — and a purely numeric value fell
  // out at the `!target` guard below before it was ever compared.
  const codeTarget = normAlnum(raw);
  if (codeTarget) {
    const byCode = options.find((o) => normAlnum(o.code) === codeTarget);
    if (byCode) return byCode.code;
  }

  const target = norm(raw);
  if (!target) return "";

  const exact = options.find((o) => norm(o.label) === target);
  if (exact) return exact.code;

  // An exact hit on one of the entry's other names. This runs before any
  // containment rule on purpose: "AMERICAN" is the United States by name, and
  // only looks like AMERICAN SAMOA by accident of spelling.
  const byAlias = options.find((o) =>
    (o.aliases || []).some((a) => norm(a) === target)
  );
  if (byAlias) return byAlias.code;

  // "U.A.E" → "uae" → initials of "United Arab Emirates".
  const byInitials = options.find((o) => initials(o.label) === target);
  if (byInitials) return byInitials.code;

  // Then the most specific label that actually appears inside the text. A
  // mulkiya reading "MERCEDES BENZ S500" contains MERCEDES, BENZ and
  // MERCEDES BENZ — all three are real entries in Orient's make list — and the
  // longest is the only one that describes the car. Same for "HONDA CIVIC",
  // where the list also carries a stray "HON".
  //
  // Deliberately one-directional. Widening this to labels that merely *contain*
  // the text would let "India" pick "British Indian Ocean Territory" purely for
  // being longer, so that direction stays under the unambiguous rule below.
  // A label only counts as "appearing in the text" if it is long enough to mean
  // something. Orient writes some models as "E-150 - 4X4" and "F150 - 4X4",
  // which reduce to "ex" and "fx" once the digits go — and "ex" sits inside
  // "fordexpedition", so a Ford Expedition resolved to an E-150.
  const MIN_FRAGMENT = 3;
  const appearsInTarget = (label) => {
    const n = norm(label);
    return n.length >= MIN_FRAGMENT && target.includes(n);
  };

  const within = options.filter((o) => appearsInTarget(o.label));
  if (within.length) {
    const longest = within.reduce((a, b) =>
      norm(b.label).length > norm(a.label).length ? b : a
    );
    const tied = within.filter(
      (o) => norm(o.label).length === norm(longest.label).length
    );
    // Two different labels of equal length both fit: no basis to choose.
    return tied.length === 1 ? longest.code : "";
  }

  // Carriers qualify a label with a body style or variant after a dash, and a
  // document never repeats that. Orient lists the Tahoe as "TAHOE - 4X4" and
  // the Aveo twice, as "AVEO - HATCHBACK" and "AVEO - SALOON", while a mulkiya
  // just says "CHEVROLET TAHOE". Compare against the part before the qualifier.
  //
  // Take the base that sits LAST in the text, not the longest. A registration
  // document names the make and then the model, and Orient's Chevrolet list
  // contains a model called "CHEVROLET - SALOON" whose base is longer than
  // "tahoe" — on length alone every single Chevrolet resolved to that one
  // wrong model. Position is what distinguishes the make from the model here.
  //
  // Short bases are ignored: a two-letter model name turns up inside all sorts
  // of unrelated text, and a wrong code is worse than none.
  const base = (label) => norm(String(label || "").split(/\s[-–—]\s|\(/)[0]);
  const qualified = options
    .map((o) => ({ option: o, b: base(o.label) }))
    .filter(({ b }) => b.length >= MIN_FRAGMENT && target.includes(b));
  if (qualified.length) {
    const best = qualified.reduce((a, c) => {
      const pa = target.lastIndexOf(a.b);
      const pc = target.lastIndexOf(c.b);
      if (pc !== pa) return pc > pa ? c : a;
      return c.b.length > a.b.length ? c : a; // same start: the fuller name
    });
    // "AVEO - HATCHBACK" vs "AVEO - SALOON": the document does not say which
    // body, so there is nothing to choose between them.
    const sharing = qualified.filter(({ b }) => b === best.b);
    return sharing.length === 1 ? best.option.code : "";
  }

  // Containment last, and only when unambiguous: "India" must not also match
  // "British Indian Ocean Territory" and silently pick the wrong one.
  //
  // The label-contains-text direction is for a text that is nearly the whole
  // label — an abbreviation or a dropped suffix — so it also demands that the
  // text account for most of the label. Without that, a mulkiya reading
  // "AMERICAN" matched AMERICAN SAMOA, being the only country starting that
  // way, and a Chevrolet was recorded as built in American Samoa. A partial
  // country name is a reason to ask, not to pick the one that starts with it.
  const NEARLY_ALL = 0.8;
  const nearlyWholeLabel = (label) => {
    const n = norm(label);
    return n.includes(target) && target.length >= n.length * NEARLY_ALL;
  };

  const contains = options.filter(
    (o) => nearlyWholeLabel(o.label) || appearsInTarget(o.label)
  );
  return contains.length === 1 ? contains[0].code : "";
};

const referenceProvider = {
  getReference,
  invalidateReference,
  matchReferenceText,
  peekReferenceLabel,
};
export default referenceProvider;
